import { Button, TextField, FormControl, FormControlLabel, Radio, RadioGroup, MenuItem, Select, InputLabel, Box, Typography, Stack, IconButton, FormLabel, Checkbox, Accordion, AccordionSummary, AccordionDetails, } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const inputProps = { size: "small", sx: { height: 40 } };

export default function HRExperienceDetails({ formData, visibleExpCount, addExperience, removeExperience, handleExpChange, handleCurrentlyWorking, handleChange, setFormData }) {

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };
    // ✅ VALIDATION HELPER FUNCTION
    const isSalaryValid = (salary) => {
        if (!salary || String(salary).length === 0) return true; // Empty is OK
        return Number(salary) >= 0;
    };

    return (
        <Stack spacing={1}>
            <FormControl required>
                <FormLabel required sx={requiredAsteriskStyle}>
                    Work Experience?
                </FormLabel>

                <RadioGroup row value={formData.hasExperience} onChange={handleChange("hasExperience")}>
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
            </FormControl>
            {formData.hasExperience === "Yes" && (

                <>
                    <Typography>Total Work Experience</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

                        {/* Years */}
                        <FormControl {...inputProps} sx={{ flex: 1, minWidth: 0, height: 40 }}>
                            <InputLabel
                                id="years-label"
                                required
                                sx={requiredAsteriskStyle}
                            >
                                Years of Experience
                            </InputLabel>

                            <Select labelId="years-label" label="Years of Experience" value={formData.experienceYears ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, experienceYears: e.target.value === '' ? null : Number(e.target.value) }))} sx={{ height: 40 }} required >
                                {[...Array(16)].map((_, i) => (
                                    <MenuItem key={i} value={i}> {i} year{i !== 1 ? 's' : ''} </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Months */}
                        <FormControl {...inputProps} sx={{ flex: 1, minWidth: 0, height: 40 }}>
                            <InputLabel
                                id="months-label"
                                required
                                sx={requiredAsteriskStyle}
                            >
                                Months
                            </InputLabel>

                            <Select
                                labelId="months-label"
                                label="Months"
                                value={formData.experienceMonths ?? ''}
                                onChange={(e) =>
                                    setFormData(prev => ({
                                        ...prev,
                                        experienceMonths: e.target.value === '' ? null : Number(e.target.value)
                                    }))
                                }
                                sx={{ height: 40 }}
                                required
                            >
                                {Array.from({ length: 12 }, (_, i) => i).map(i => (
                                    <MenuItem key={i} value={i}>
                                        {i} month{i !== 1 ? 's' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    {formData.experienceDetails
                        .slice(0, visibleExpCount)
                        .map((exp, index) => (
                            <Accordion key={index} defaultExpanded={index === 0}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography fontWeight={"bold"}> Experience {index + 1} </Typography>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Stack spacing={2}>

                                        {/* ROW 1: Company Name + Job Title */}
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <TextField label="Company Name" InputLabelProps={{ sx: requiredAsteriskStyle }} {...inputProps} sx={{ flex: 1 }} value={exp["companyName"]} onChange={(e) => handleExpChange(index, "companyName", e.target.value)} required />
                                            <TextField label="Job Title" InputLabelProps={{ sx: requiredAsteriskStyle }} {...inputProps} sx={{ flex: 1 }} value={exp["jobTitle"]} onChange={(e) => handleExpChange(index, "jobTitle", e.target.value)} required />
                                        </Box>

                                        {/* ROW 2: Salary + From Date + To Date + Currently Working */}
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                            <TextField label="Salary (₹/month)" type="number" InputLabelProps={{ sx: requiredAsteriskStyle }} {...inputProps} sx={{ flex: 1 }} value={exp["salaryPerMonth"]} onChange={(e) => { const value = e.target.value; if (value === "" || Number(value) >= 0) { handleExpChange(index, "salaryPerMonth", value); } }} required inputProps={{ min: 0 }} error={!isSalaryValid(exp["salaryPerMonth"])} helperText={!isSalaryValid(exp["salaryPerMonth"]) ? "Salary cannot be negative" : ""} />
                                            <DatePicker label="From" value={exp.fromDate ? dayjs(exp.fromDate) : null} onChange={(newValue) => handleExpChange(index, "fromDate", newValue ? newValue.format("YYYY-MM-DD") : "")} format="DD/MM/YYYY" slotProps={{ textField: { ...inputProps, sx: { flex: 1 }, required: true, InputLabelProps: { sx: requiredAsteriskStyle }, } }} />
                                            <DatePicker label="To Date" value={exp.toDate ? dayjs(exp.toDate) : null} onChange={(newValue) => handleExpChange(index, "toDate", newValue ? newValue.format("YYYY-MM-DD") : "")} format="DD/MM/YYYY" disabled={exp.currentlyWorking} slotProps={{ textField: { ...inputProps, sx: { flex: 1 } }, InputLabelProps: { sx: requiredAsteriskStyle }, }} />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox checked={exp.currentlyWorking} onChange={(e) => handleCurrentlyWorking(index, e.target.checked)} />
                                                }
                                                label="Currently Working"
                                                sx={{ whiteSpace: "nowrap" }}
                                            />
                                        </Box>

                                        {/* ROW 3: Reason for Leaving */}
                                        <TextField label="Reason for Leaving" multiline minRows={1.5} fullWidth value={exp["reasonForLeaving"]} onChange={(e) => handleExpChange(index, "reasonForLeaving", e.target.value)} required InputLabelProps={{ sx: requiredAsteriskStyle }} />

                                        {/* REMOVE BUTTON */}
                                        {(visibleExpCount > 1 && index > 0) && (
                                            <Box textAlign="right">
                                                <IconButton color="error" onClick={() => removeExperience(index)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        ))}

                    {/* ADD MORE BUTTON */}
                    <Box>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={addExperience} sx={{ width: 'auto' }} > Add More Experience </Button>
                    </Box>
                </>
            )}
        </Stack>
    );
}